import {
  email, helpers, minLength, required,
} from 'vuelidate/lib/validators';

/**
 * Get maximum characters for the input
 * @param inputName {String}
 * @return {Number}
 */
const getAllowedMaximumCharacters = function getAllowedMaxCharacters(inputName) {
  const allowedMaximumCharacters = {
    email: 100,
    name: 50,
    password: 64,
    fixes: 5,
    zip_code: 5,
    company_name: 50,
    address: 100,
    city: 40,
  };
  return allowedMaximumCharacters[inputName] || 50;
};

/**
 * Custom validator for vuelidate - check maxLength according to the input's name
 * @param inputName {String}
 */
const maxLengthByInputName = (inputName) => helpers.withParams(
  { type: 'maxLength', max: getAllowedMaximumCharacters(inputName) },
  (value) => helpers.len(value) <= getAllowedMaximumCharacters(inputName),
);

/**
 * Custom validator for vuelidate - check whitespaces on the both ends of a string
 * @param string {String}
 */
const whiteSpacing = (string) => string.length === string.trim().length;

/**
 * Custom validator for phone number format
 */
const phoneFormat = helpers.withParams(
  { type: 'phoneFormat' },
  (value) => /^\d{3}-\d{3}-\d{4}$/.test(value) || /^\d{10}$/.test(value),
);

/**
 * Custom validator for credit card format
 */
const creditCardFormat = helpers.withParams(
  { type: 'creditCardFormat' },
  (value) => {
    const twoSymbols = Number(value.slice(0, 2));
    const max = (twoSymbols === 34 || twoSymbols === 37) ? 15 : 16;
    const regex = new RegExp(`^\\d{${max}}$`);
    return regex.test(value);
  },
);

/**
 * Checking for special symbols
 */
const specialSymbols = () => helpers.withParams(
  { type: 'specialSymbols' },
  (value) => !/[#$%^&*"'<>?]/.test(value),
);

/**
 * Custom validator for vuelidate - check for at least one lowercase letter
 * and at least one uppercase letter.
 * @param string {String}
 */
const uppercaseAndLowercaseLetters = (string) => {
  const regex = /(?=.*[a-z])(?=.*[A-Z])/;
  return regex.test(string);
};

/**
 * Custom validator for vuelidate - check if only letters
 * @param value {String}
 */
const onlyLettersValidator = (value) => !value || /^[a-zA-Z]+$/.test(value);

/**
 * Custom validator for vuelidate - check if the local part of email (before the "@") contains 64 characters or less
 * and domain part of email (after the "@") contains 255 characters or less
 * @param value {String}
 */
const maxEmailPartsLengthValidator = (value) => {
  if (!value) return true;
  const [localPart, domainPart] = value.split('@');
  return localPart.length <= 64 && domainPart.length <= 255;
};

/**
 * Custom validator for vuelidate - check if phone format is (xxx) xxx-xxxx or xxx-xxx-xxxx or xxxxxxxxxx
 * @param value {String}
 */
const phoneFormatValidator = (value) => {
  if (!value) return true;
  return /^(\(\d{3}\)\s?\d{3}[-\s]?\d{4}|\d{3}[-\s]?\d{3}[-\s]?\d{4}|\d{10})$/.test(value);
};

/**
 * Custom validator for vuelidate - check if url is correct
 * @param value {String}
 */
const urlFormatValidator = (value) => !value
  || /^(https?:\/\/)?([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}(\/\S*)?$/i.test(value);

/**
 * Custom validator for vuelidate - check for at least one number or special character.
 * @param string {String}
 */
const digitOrSpecialChar = (string) => {
  const regex = /[\d\W]/;
  return regex.test(string);
};
const passwordValidation = () => ({
  required,
  minLength: minLength(6),
  maxLength: maxLengthByInputName('password'),
  whiteSpacing,
});
const securedPasswordValidation = () => ({
  required,
  minLength: minLength(8),
  maxLength: maxLengthByInputName('password'),
  whiteSpacing,
  uppercaseAndLowercaseLetters,
  digitOrSpecialChar,
});
const emailValidation = () => ({
  required,
  email,
  maxLength: maxLengthByInputName('email'),
  specialSymbols,
});
const nameValidation = () => ({
  required,
  maxLength: maxLengthByInputName('name'),
});
const addressValidation = () => ({
  required,
  minLength: minLength(5),
  maxLength: maxLengthByInputName('address'),
});
const apartmentValidation = () => ({
  maxLength: maxLengthByInputName('address'),
});
const cityValidation = () => ({
  required,
  maxLength: maxLengthByInputName('city'),
});
const zipValidation = () => ({
  required,
  minLength: minLength(5),
  maxLength: maxLengthByInputName('zip_code'),
});
const phoneValidation = () => ({
  required,
  phoneFormat,
});
const creditCardValidation = () => ({
  required,
  creditCardFormat,
});

const maxLengthMessage = (n) => `Maximum allowed characters ${n === 1 ? 'is' : 'are'} ${n}.`;
const addressLengthMessage = (n) => `Street Address must be between 5 - ${n} characters.`;
const postcodeLengthMessage = (n) => `ZIP code must be ${n} characters.`;
const whiteSpacingMessage = 'Please clean leading or trailing spaces.';
const requiredMessage = 'This is a required field.';
const uppercaseAndLowercaseLettersMessage = 'Please ensure your password contains both uppercase and lowercase letters.';
const digitOrSpecialCharMessage = 'Please ensure your password contains at least one number or special character.';

const validationMixin = {
  data() {
    return {
      captchaError: null,
    };
  },
  computed: {
    zipCodeErrors() {
      const errors = [];
      if (!this.$v.zipCode?.$dirty) { return errors; }
      if (!this.$v.zipCode.minLength) {
        errors.push(`ZIP Code must have at least ${this.$v.zipCode.$params?.minLength?.min} symbols.`);
      }
      if (!this.$v.zipCode.checkValid) {
        errors.push('ZIP Code failed validation.');
      }
      if (!this.$v.zipCode.required) {
        errors.push('ZIP Code is required.');
      }
      return errors;
    },
    ratingErrors() {
      const errors = [];
      if (!this.$v.rating?.$dirty) { return errors; }
      if (!this.$v.rating.between) {
        errors.push('The rating must be between 1 and 5.');
      }
      if (!this.$v.rating.required) {
        errors.push('Please select one of each of the ratings above.');
      }
      return errors;
    },
    requiredErrors() {
      return (name) => {
        const errors = [];
        if (!this.$v[name]?.$dirty) { return errors; }
        if (!this.$v[name].required) {
          errors.push(requiredMessage);
        }
        return errors;
      };
    },
  },
  methods: {
    validateForm(validators) {
      const errorsArray = [];
      const entries = Object.entries(validators);
      entries.forEach((entry) => {
        const [name, errors] = entry;
        if (errors.length) {
          errorsArray.push({ name, errors });
        }
      });
      this.errors = errorsArray;
    },
    checkIsInputInvalid(input) {
      if (!input) { return false; }
      return input.$invalid || false;
    },
    validatePassword(input, isSecured, handler) {
      const innerHandler = (errors) => {
        if (!input) { return; }
        if (!input.minLength) {
          errors.push(
            `Please ensure your password has between 
            ${input.$params?.minLength?.min} and ${input.$params?.maxLength?.max} characters.`,
          );
        }
        if (!input.required) {
          errors.push(requiredMessage);
        }
        if (!input.whiteSpacing) {
          errors.push(whiteSpacingMessage);
        }
        if (!input.maxLength) {
          // ToDo return message after removing magento AuthPopup
          // errors.push(maxLengthMessage(input.$params?.maxLength?.max));
          errors.push(
            `Please ensure your password has between 
            ${input.$params?.minLength?.min} and ${input.$params?.maxLength?.max} characters.`,
          );
        }
        if (isSecured && !input.uppercaseAndLowercaseLetters) {
          errors.push(uppercaseAndLowercaseLettersMessage);
        }

        if (isSecured && !input.digitOrSpecialChar) {
          errors.push(digitOrSpecialCharMessage);
        }
        if (typeof handler === 'function') {
          handler(errors);
        }
      };
      return this.getValidatorErrors(input, innerHandler) || [];
    },
    validateEmail(input, handler) {
      const innerHandler = (errors) => {
        if (!input) { return; }
        if (!input.email) {
          errors.push('Please enter a valid email address.');
        }
        if (!input.required) {
          errors.push(requiredMessage);
        }
        if (!input.maxLength) {
          errors.push(maxLengthMessage(input.$params?.maxLength?.max));
        }
        if (!input.specialSymbols) {
          errors.push('Please enter a valid email address.');
        }
        if (typeof handler === 'function') {
          handler(errors);
        }
      };
      return this.getValidatorErrors(input, innerHandler) || [];
    },
    validateName(input, handler) {
      const innerHandler = (errors) => {
        if (!input) { return; }
        if (!input.required) {
          errors.push(requiredMessage);
        }
        if (!input.maxLength) {
          errors.push(maxLengthMessage(input.$params?.maxLength?.max));
        }
        if (typeof handler === 'function') {
          handler(errors);
        }
      };
      return this.getValidatorErrors(input, innerHandler) || [];
    },
    validateAddress(input, handler) {
      const innerHandler = (errors) => {
        if (!input) { return; }
        if (!input.required) {
          errors.push(requiredMessage);
        }
        if (!input.maxLength || !input.minLength) {
          errors.push(addressLengthMessage(input.$params?.maxLength?.max));
        }
        if (typeof handler === 'function') {
          handler(errors);
        }
      };
      return this.getValidatorErrors(input, innerHandler) || [];
    },
    validateApartment(input, handler) {
      const innerHandler = (errors) => {
        if (!input) { return; }
        if (!input.maxLength) {
          errors.push(maxLengthMessage(input.$params?.maxLength?.max));
        }
        if (typeof handler === 'function') {
          handler(errors);
        }
      };
      return this.getValidatorErrors(input, innerHandler) || [];
    },
    validateCity(input, handler) {
      const innerHandler = (errors) => {
        if (!input) { return; }
        if (!input.required) {
          errors.push(requiredMessage);
        }
        if (!input.maxLength) {
          errors.push(maxLengthMessage(input.$params?.maxLength?.max));
        }
        if (typeof handler === 'function') {
          handler(errors);
        }
      };
      return this.getValidatorErrors(input, innerHandler) || [];
    },
    validateZip(input, handler) {
      const innerHandler = (errors) => {
        if (!input || !input.$dirty) { return; }
        if (!input.required) {
          errors.push(requiredMessage);
        }
        if (!input.maxLength || !input.minLength) {
          errors.push(postcodeLengthMessage(input.$params?.maxLength?.max));
        }
        if (input.$model === '00000') {
          errors.push('Invalid zip code.');
        }
        if (typeof handler === 'function') {
          handler(errors);
        }
      };
      return this.getValidatorErrors(input, innerHandler) || [];
    },
    validatePhone(input, handler) {
      const innerHandler = (errors) => {
        if (!input) { return; }
        if (!input.required) {
          errors.push(requiredMessage);
        }
        const phoneRegex = /^\d{3}-\d{3}-\d{4}$|^\d{10}$/;
        if (!phoneRegex.test(input.$model)) {
          errors.push('Please enter a valid phone number. For example 123-456-7890.');
        }
        if (typeof handler === 'function') {
          handler(errors);
        }
      };
      return this.getValidatorErrors(input, innerHandler) || [];
    },
    validateCreditCardName(input, handler) {
      const innerHandler = (errors) => {
        if (!input) { return; }
        if (!input.required) {
          errors.push(requiredMessage);
        }
        if (!input.maxLength) {
          errors.push(maxLengthMessage(input.$params?.maxLength?.max));
        }
        if (typeof handler === 'function') {
          handler(errors);
        }
      };
      return this.getValidatorErrors(input, innerHandler) || [];
    },
    validateCreditCard(input, handler) {
      const innerHandler = (errors) => {
        if (!input) { return; }
        if (!input.required) {
          errors.push(requiredMessage);
        }
        if (typeof handler === 'function') {
          handler(errors);
        }
      };
      return this.getValidatorErrors(input, innerHandler) || [];
    },
    validateCreditCardCVV(input, handler) {
      const innerHandler = (errors) => {
        if (!input) { return; }
        if (!input.required) {
          errors.push(requiredMessage);
        }
        if (typeof handler === 'function') {
          handler(errors);
        }
      };
      return this.getValidatorErrors(input, innerHandler) || [];
    },
    validateRequired(input, handler) {
      const innerHandler = (errors) => {
        if (!input) { return; }
        if (!input.required) {
          errors.push(requiredMessage);
        }
        if (typeof handler === 'function') {
          handler(errors);
        }
      };
      return this.getValidatorErrors(input, innerHandler) || [];
    },
    getValidatorErrors(input, handler) {
      const errors = [];
      if (!input?.$dirty) { return errors; }
      if (typeof handler === 'function') {
        handler(errors);
      }
      if (!(Array.isArray(errors) && errors.length) && this.checkIsInputInvalid(input)) {
        errors.push('');
      }
      return errors || [];
    },
    /**
         * Find an input in the first ref with errors and focus on in.
         * Dependency: this.$refs
         * @param namesArray {Array} - inputs names in the correct order ['email', 'password'].
         * Use them for refs and validators keys.
         * @param validators {Object} - {password: this.passwordErrors, email: this.emailErrors} form errors.
         * Use input names as keys.
         */
    focusOnErrorInput(namesArray, validators) {
      const findFullArrayInValues = function findFullArrayInValuesFunction(name, obj) {
        return Object.entries(obj).find(([key, value]) => {
          if (Array.isArray(value)) {
            return value.length && key === name;
          }
          if (typeof value === 'object') {
            return findFullArrayInValues(name, value);
          }
          return false;
        });
      };
      const inputName = namesArray.find((name) => findFullArrayInValues(name, validators));
      this.focusOnInput(inputName);
    },
    /**
         * Find an input and focus on in.
         * Dependency: this.$refs
         * @param inputName {String} - input's name. Use it for refs and validators keys.
         */
    focusOnInput(inputName) {
      if (inputName && this.$refs) {
        const input = this.$refs[inputName]?.$el?.querySelector('input');
        if (input) {
          input.focus();
        }
      }
    },
    baseValidator(value, rules) {
      const errors = [];
      rules.forEach((rule) => {
        const result = rule(value);
        if (result !== true) {
          errors.push(result);
        }
      });
      return errors;
    },
    /** @TODO: check recaptcha */
    async checkCaptcha() {
      try {
        this.captchaError = null;
        const token = await this.$recaptcha?.getResponse();
        if (!token) {
          throw new Error('Failed to execute');
        }
        return token;
      } catch (e) {
        if (e === 'Failed to execute') {
          this.captchaError = 'Please fill up the captcha.';
        } else {
          this.captchaError = 'Something goes wrong with the captcha. Please try again later.';
        }
      }
      return null;
    },
  },
};

export {
  maxLengthByInputName,
  whiteSpacing,
  onlyLettersValidator,
  maxEmailPartsLengthValidator,
  phoneFormatValidator,
  urlFormatValidator,
  passwordValidation,
  securedPasswordValidation,
  emailValidation,
  specialSymbols,
  nameValidation,
  addressValidation,
  apartmentValidation,
  cityValidation,
  zipValidation,
  phoneValidation,
  creditCardValidation,
};

export default validationMixin;
